import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { FriendPresence } from '@il-colpo/protocol';
import { handleOf } from '@il-colpo/shared/account';
import { AvatarFigure } from '../../account/AvatarFigure.tsx';
import { useAccount } from '../../account/accountStore.ts';
import { useLooseT } from '../../game/text/loose.ts';
import { useMetaClient, useRemote } from '../../meta/hooks.ts';
import type { FriendMatch } from '../../meta/types.ts';
import { useFlag } from '../../meta/liveStore.ts';
import { useSocialStore } from '../../social/socialStore.ts';
import type { AppNotification, MyRestriction, Person, RequestOutcome } from '../../social/types.ts';
import { useDateFormat } from '../account/common.tsx';
import { useMetaGate, type MetaGate } from '../meta/common.tsx';
import { Badge, Button, Panel, ScreenFrame, TextField } from '../../ui/index.ts';
import styles from './Social.module.css';

const presenceTone = (state: FriendPresence['state']) =>
  state === 'online'
    ? 'green'
    : state === 'lobby'
      ? 'cyan'
      : state === 'playing'
        ? 'amber'
        : 'neutral';

/** The latest heists of a friend that can still be watched, each with a way to watch it from the friend's seat. */
function FriendHeists({ userId }: { userId: string }) {
  const { t } = useTranslation('social');
  const g = useLooseT('game');
  const navigate = useNavigate();
  const { date } = useDateFormat();
  const meta = useMetaClient();
  const load = useMemo(() => (meta ? () => meta.friendMatches(userId, 8) : null), [meta, userId]);
  const heists = useRemote<FriendMatch[]>(load);
  if (heists.status === 'loading') {
    return (
      <p className={styles['note']} role="status">
        {t('gate.loading')}
      </p>
    );
  }
  if (heists.status === 'error') {
    return (
      <p className={styles['error']} role="alert">
        {t('friends.heistsFailed')}
      </p>
    );
  }
  const list = heists.data ?? [];
  if (list.length === 0) return <p className={styles['note']}>{t('friends.heistsEmpty')}</p>;
  return (
    <ul className={styles['list']} data-testid="friend-heists">
      {list.map((m) => (
        <li key={m.id} className={styles['person']}>
          <span className={styles['personName']}>
            <strong>{g(`maps.${m.mapId}.name`)}</strong>
            <span className={styles['heistInfo']}>
              <Badge tone={m.success ? 'green' : 'pink'}>
                {m.success ? t('friends.heistDone') : t('friends.heistFailed')}
              </Badge>
              <span className={styles['note']}>
                {t('friends.heistRole', { role: g(`roles.${m.role}`) })}
              </span>
              <span className={styles['note']}>{date(m.endedAt)}</span>
            </span>
          </span>
          <Button
            variant="secondary"
            onClick={() => {
              void navigate(`/replay/${m.id}?seat=${m.seat}&from=friends`);
            }}
          >
            {t('friends.watch')}
          </Button>
        </li>
      ))}
    </ul>
  );
}

function FriendRow({
  person,
  presence,
  busy,
  onRemove,
  onBlock,
  onJoin,
}: {
  person: Person;
  presence: FriendPresence | undefined;
  busy: boolean;
  onRemove: () => void;
  onBlock: () => void;
  onJoin: (code: string) => void;
}) {
  const { t } = useTranslation('social');
  const [open, setOpen] = useState(false);
  const state = presence?.state ?? 'offline';
  return (
    <li className={styles['friend']} data-testid="friend">
      <div className={styles['person']}>
        <AvatarFigure avatar={person.avatar} size="2.6rem" />
        <span className={styles['personName']}>
          <strong>{handleOf(person.nickname, person.tag)}</strong>
          <span>
            <Badge tone={presenceTone(state)}>{t(`friends.presence.${state}`)}</Badge>
          </span>
        </span>
        <span className={styles['actions']}>
          {presence?.state === 'lobby' && presence.code ? (
            <Button
              onClick={() => {
                onJoin(presence.code as string);
              }}
            >
              {t('friends.join')}
            </Button>
          ) : null}
          <Button
            variant="ghost"
            aria-expanded={open}
            onClick={() => {
              setOpen((o) => !o);
            }}
          >
            {open ? t('friends.heistsClose') : t('friends.heists')}
          </Button>
          <Button variant="ghost" disabled={busy} onClick={onRemove}>
            {t('friends.remove')}
          </Button>
          <Button variant="ghost" disabled={busy} onClick={onBlock}>
            {t('friends.block')}
          </Button>
        </span>
      </div>
      {open ? <FriendHeists userId={person.userId} /> : null}
    </li>
  );
}

function Notices({ restrictions }: { restrictions: MyRestriction[] }) {
  const { t } = useTranslation('social');
  const { date } = useDateFormat();
  if (restrictions.length === 0) return null;
  return (
    <Panel title={t('notices.title')}>
      <ul className={styles['list']}>
        {restrictions.map((r) => (
          <li key={`${r.kind}-${r.createdAt}`} className={styles['person']}>
            <span className={styles['personName']}>
              <strong>{t(`notices.${r.kind}`)}</strong>
              <span className={styles['note']}>
                {r.untilAt ? t('notices.until', { date: date(r.untilAt) }) : t('notices.forever')}
                {r.reason ? ` ${t('notices.reason', { reason: r.reason })}` : ''}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function Latest({ notifications }: { notifications: AppNotification[] }) {
  const { t } = useTranslation('social');
  const { date } = useDateFormat();
  if (notifications.length === 0) return null;
  return (
    <Panel title={t('notifications.title')}>
      <ul className={styles['list']}>
        {notifications.slice(0, 6).map((n) => (
          <li key={n.id} className={styles['person']}>
            <span className={styles['personName']}>
              <span>
                {t(`notifications.${n.kind}`, {
                  name: n.nickname === null ? '' : handleOf(n.nickname, n.tag ?? 0),
                })}
              </span>
              <span className={styles['note']}>{date(n.createdAt)}</span>
            </span>
            {!n.read ? <span className={styles['unreadDot']} aria-hidden="true" /> : null}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/** What the screen shows in place of its content when there is no account to read friends from. */
function Gate({ gate }: { gate: Exclude<MetaGate, 'ready'> }) {
  const { t } = useTranslation('social');
  const navigate = useNavigate();
  if (gate === 'loading') {
    return (
      <Panel>
        <p className={styles['note']} role="status">
          {t('gate.loading')}
        </p>
      </Panel>
    );
  }
  if (gate === 'unavailable') {
    return (
      <Panel title={t('gate.unavailable')}>
        <p className={styles['note']}>{t('gate.unavailableBody')}</p>
      </Panel>
    );
  }
  return (
    <Panel title={t('gate.signIn')}>
      <div className={styles['stack']}>
        <p className={styles['note']}>{t('gate.signInBody')}</p>
        <div>
          <Button
            onClick={() => {
              void navigate('/account');
            }}
          >
            {t('gate.signInAction')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

/** `/friends`: the friends and who is here, the requests, asking somebody, the blocked, and the notices. */
export function FriendsScreen() {
  const { t } = useTranslation('social');
  const navigate = useNavigate();
  const gate = useMetaGate();
  const guest = useAccount((s) => s.snapshot.user?.anonymous ?? false);
  const client = useAccount((s) => s.client);
  const enabled = useFlag('friends');
  const ready = gate === 'ready' && !guest && enabled && client !== null;

  const overview = useSocialStore((s) => s.overview);
  const status = useSocialStore((s) => s.overviewStatus);
  const presence = useSocialStore((s) => s.presence);
  const notifications = useSocialStore((s) => s.notifications);

  const [handle, setHandle] = useState('');
  const [outcome, setOutcome] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const social = client?.social() ?? null;
  const loadRestrictions = useMemo(
    () => (ready && social ? () => social.restrictions() : null),
    [ready, social],
  );
  const restrictions = useRemote<MyRestriction[]>(loadRestrictions);

  const refresh = useCallback(async () => {
    const store = useSocialStore.getState();
    await store.refreshOverview(true);
    await store.syncPresence();
  }, []);

  useEffect(() => {
    if (!ready) return;
    void refresh();
    void useSocialStore.getState().refreshNotifications(true);
  }, [ready, refresh]);

  // What is on the page counts as read.
  const unread = notifications.some((n) => !n.read);
  useEffect(() => {
    if (!ready || !unread) return;
    const id = setTimeout(() => {
      void useSocialStore.getState().markNotificationsRead();
    }, 1500);
    return () => {
      clearTimeout(id);
    };
  }, [ready, unread]);

  const run = async (fn: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    try {
      await fn();
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const ask = async (e: FormEvent) => {
    e.preventDefault();
    const text = handle.trim();
    if (!text || !social) return;
    setBusy(true);
    setOutcome(null);
    const r = await social.request(text);
    if (r.ok) {
      const kind: RequestOutcome = r.value;
      setOutcome({
        kind: kind === 'requested' || kind === 'accepted' ? 'ok' : 'error',
        text: t(`add.outcome.${kind}`, { name: text }),
      });
      if (kind === 'requested' || kind === 'accepted') setHandle('');
      await refresh();
    } else {
      setOutcome({ kind: 'error', text: t('add.failed') });
    }
    setBusy(false);
  };

  const content = (() => {
    if (!ready) return null;
    if (status === 'failed') {
      return (
        <Panel>
          <div className={styles['stack']}>
            <p className={styles['error']} role="alert">
              {t('gate.failed')}
            </p>
            <div>
              <Button
                variant="secondary"
                onClick={() => {
                  void refresh();
                }}
              >
                {t('gate.retry')}
              </Button>
            </div>
          </div>
        </Panel>
      );
    }
    if (!overview) {
      return (
        <Panel>
          <p className={styles['note']} role="status">
            {t('gate.loading')}
          </p>
        </Panel>
      );
    }
    return (
      <>
        <Panel title={t('add.title')}>
          <form className={styles['addForm']} onSubmit={(e) => void ask(e)} noValidate>
            <TextField
              className={styles['addField']}
              label={t('add.label')}
              hint={t('add.hint')}
              value={handle}
              maxLength={24}
              autoComplete="off"
              spellCheck={false}
              onChange={setHandle}
            />
            <Button type="submit" loading={busy} disabled={handle.trim().length < 6}>
              {t('add.action')}
            </Button>
          </form>
          {outcome ? (
            <p
              className={outcome.kind === 'ok' ? styles['ok'] : styles['error']}
              role={outcome.kind === 'ok' ? 'status' : 'alert'}
            >
              {outcome.text}
            </p>
          ) : null}
        </Panel>

        {overview.incoming.length + overview.outgoing.length > 0 ? (
          <Panel title={t('requests.title')}>
            <ul className={styles['list']}>
              {overview.incoming.map((p) => (
                <li key={p.userId} className={styles['person']} data-testid="request-in">
                  <AvatarFigure avatar={p.avatar} size="2.6rem" />
                  <span className={styles['personName']}>
                    {t('requests.incoming', { name: handleOf(p.nickname, p.tag) })}
                  </span>
                  <span className={styles['actions']}>
                    <Button
                      disabled={busy}
                      onClick={() => {
                        void run(() => social?.respond(p.userId, true) ?? Promise.resolve());
                      }}
                    >
                      {t('requests.accept')}
                    </Button>
                    <Button
                      variant="ghost"
                      disabled={busy}
                      onClick={() => {
                        void run(() => social?.respond(p.userId, false) ?? Promise.resolve());
                      }}
                    >
                      {t('requests.decline')}
                    </Button>
                  </span>
                </li>
              ))}
              {overview.outgoing.map((p) => (
                <li key={p.userId} className={styles['person']} data-testid="request-out">
                  <AvatarFigure avatar={p.avatar} size="2.6rem" />
                  <span className={styles['personName']}>
                    {t('requests.outgoing', { name: handleOf(p.nickname, p.tag) })}
                  </span>
                  <span className={styles['actions']}>
                    <Button
                      variant="ghost"
                      disabled={busy}
                      onClick={() => {
                        void run(() => social?.remove(p.userId) ?? Promise.resolve());
                      }}
                    >
                      {t('requests.cancel')}
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          </Panel>
        ) : null}

        <Panel title={t('friends.title')}>
          {overview.friends.length === 0 ? (
            <p className={styles['note']}>{t('friends.empty')}</p>
          ) : (
            <ul className={styles['list']}>
              {overview.friends.map((p) => (
                <FriendRow
                  key={p.userId}
                  person={p}
                  presence={presence[p.userId]}
                  busy={busy}
                  onRemove={() => {
                    void run(() => social?.remove(p.userId) ?? Promise.resolve());
                  }}
                  onBlock={() => {
                    void run(() => social?.block(handleOf(p.nickname, p.tag)) ?? Promise.resolve());
                  }}
                  onJoin={(code) => {
                    void navigate(`/online/join/${code}`);
                  }}
                />
              ))}
            </ul>
          )}
        </Panel>

        <Latest notifications={notifications} />
        {restrictions.data ? <Notices restrictions={restrictions.data} /> : null}

        <Panel title={t('blocked.title')}>
          <div className={styles['stack']}>
            {overview.blocked.length === 0 ? (
              <p className={styles['note']}>{t('blocked.empty')}</p>
            ) : (
              <ul className={styles['list']}>
                {overview.blocked.map((b) => (
                  <li key={b.userId} className={styles['person']} data-testid="blocked">
                    <span className={styles['personName']}>
                      <strong>{handleOf(b.nickname, b.tag)}</strong>
                    </span>
                    <span className={styles['actions']}>
                      <Button
                        variant="ghost"
                        disabled={busy}
                        onClick={() => {
                          void run(() => social?.unblock(b.userId) ?? Promise.resolve());
                        }}
                      >
                        {t('blocked.unblock')}
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className={styles['note']}>{t('blocked.note')}</p>
          </div>
        </Panel>
      </>
    );
  })();

  return (
    <ScreenFrame title={t('title')} backTo="/online">
      <div className={styles['page']}>
        {gate !== 'ready' ? (
          <Gate gate={gate} />
        ) : guest ? (
          <Panel title={t('gate.guest')}>
            <p className={styles['note']}>{t('gate.guestBody')}</p>
          </Panel>
        ) : !enabled ? (
          <Panel>
            <p className={styles['note']}>{t('gate.off')}</p>
          </Panel>
        ) : (
          <p className={styles['note']}>{t('intro')}</p>
        )}
        {content}
      </div>
    </ScreenFrame>
  );
}
